/**
 * @file Hash change event controller
 * ハッシュ変更イベントを集約して管理するコントローラー。
 */

/**
 * 任意ハッシュで発火するイベント定義。
 * @typedef {Object} HashEvent
 * @property {Function} fn 実行するコールバック。引数に現在のhash文字列を受け取る。
 * @property {Object} [parent=window] fn実行時のthisコンテキスト。
 * @property {number|boolean} [first] trueの場合は登録時に即時実行。数値の場合はそのms遅延後に実行。
 */

/**
 * ハッシュ指定イベント定義。
 * @typedef {Object} HashByHashEvent
 * @property {Function} fn 実行するコールバック。引数に現在のhash文字列を受け取る。
 * @property {Object} [parent=window] fn実行時のthisコンテキスト。
 * @property {string} hash 対象となるhash（`#` なし）。
 */

/**
 * hashChangeController 公開API。
 * @typedef {Object} HashChangeController
 * @property {(event?: HashEvent) => void} setEvent
 * @property {(events?: HashByHashEvent[]) => void} setEventByHash
 * @property {() => void} destroy
 */

/** @type {HashChangeController} */
export const hashChangeController = (() => {
    /** @type {{fn: Function, parent: Object}[]} */
    const hashChangeEvents = [];

    /** @type {Map<string, {fn: Function, parent: Object}[]>} */
    const hashChangeEventsByHash = new Map();

    /** @type {() => void} */
    const onHashChange = () => runHashChangeEvents();

    if (typeof window !== 'undefined') {
        window.addEventListener('hashchange', onHashChange, false);
    }

    /**
     * ハッシュ変更時に常に実行されるイベントを登録する。
     * `first` 指定時は登録タイミングでも実行可能。
     *
     * @param {HashEvent} [event={}] イベント設定オブジェクト
     * @returns {void}
     */
    function setEvent({ fn, parent = window, first } = {}) {
        if (typeof fn !== 'function') return;

        hashChangeEvents.push({ fn, parent });

        if (first) {
            const hash = getHash();
            const delay = Number(first);
            if (Number.isFinite(delay)) {
                setTimeout(() => safeCall(fn, parent, hash), delay);
            } else {
                safeCall(fn, parent, hash);
            }
        }
    }

    /**
     * 特定hashに紐づくイベント群を登録する。
     * 現在hashと一致する定義は登録時点でも即時実行する。
     *
     * @param {HashByHashEvent[]} [list=[]] ハッシュ連動イベント配列
     * @returns {void}
     */
    function setEventByHash(list = []) {
        if (!Array.isArray(list) || list.length === 0) return;

        const current = getHash();
        list.forEach(({ fn, parent = window, hash }) => {
            if (typeof fn !== 'function' || !hash) return;

            const arr = hashChangeEventsByHash.get(hash) ?? [];
            arr.push({ fn, parent });
            hashChangeEventsByHash.set(hash, arr);

            if (current === hash) safeCall(fn, parent, current);
        });
    }

    /**
     * 登録済みのハッシュイベントを実行する。
     *
     * @returns {void}
     */
    function runHashChangeEvents() {
        const hash = getHash();

        hashChangeEvents.forEach(({ fn, parent }) => {
            safeCall(fn, parent, hash);
        });

        const byHash = hashChangeEventsByHash.get(hash) ?? [];
        byHash.forEach(({ fn, parent }) => {
            safeCall(fn, parent, hash);
        });
    }

    /**
     * コールバックを安全に呼び出す。
     *
     * @param {Function} fn 実行関数
     * @param {Object} parent thisコンテキスト
     * @param {string} hash 現在hash
     * @returns {void}
     */
    function safeCall(fn, parent, hash) {
        try {
            fn.call(parent, hash);
        } catch (e) {
            console.error('[hashChangeController] callback failed', e);
        }
    }

    /**
     * 現在のhashを取得する。
     * `_escaped_fragment_` クエリがある場合はそれを優先する。
     *
     * @returns {string} `#` なしのhash文字列
     */
    function getHash() {
        let ret = '';
        if (typeof location !== 'undefined' && location.search) {
            const [k, v] = location.search.replace('?', '').split('=');
            if (k === '_escaped_fragment_') ret = `!${v ?? ''}`;
        }
        if (!ret && typeof location !== 'undefined') {
            ret = location.hash ? location.hash.replace('#', '') : '';
        }
        return ret;
    }

    /**
     * リスナー解除と内部イベントの破棄を行う。
     *
     * @returns {void}
     */
    function destroy() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('hashchange', onHashChange, false);
        }
        hashChangeEvents.length = 0;
        hashChangeEventsByHash.clear();
    }

    return {
        setEvent,
        setEventByHash,
        destroy,
    };
})();
